import { useParams } from "react-router-dom";
import SiteShell from "@/components/site/SiteShell";
import { SeoHead } from "@/components/seo/SeoHead";
import { Breadcrumb } from "@/components/Breadcrumb";
import { useLocale } from "@/locale/LocaleProvider";
import { pickOfferSite, type OfferCard } from "@/locale/content/offerSite";
import { SITE_TELEGRAM_URL } from "@/lib/siteContact";
import { trackContactClick } from "@/lib/analytics";
import { ArrowRight, Check } from "lucide-react";
import { LocalizedLink } from "@/components/LocalizedLink";

const IDS: OfferCard["id"][] = ["incident", "audit", "retainer", "projects"];

export default function OfferServicePage({ serviceId }: { serviceId?: OfferCard["id"] }) {
  const { lang, t } = useLocale();
  const params = useParams();
  const fromParam = params.serviceId as OfferCard["id"] | undefined;
  const id = serviceId ?? fromParam;
  const site = pickOfferSite(lang);
  const page = id && IDS.includes(id) ? site.services[id] : null;

  if (!page) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <p className="text-[var(--a-muted)]">{t("notFound.h1")}</p>
          <LocalizedLink to="/" className="mt-4 inline-block text-[var(--a-primary)] hover:text-[var(--a-primary-hover)]">
            {t("notFound.home")}
          </LocalizedLink>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <SeoHead basePath={page.path} title={page.seo.title} description={page.seo.description} lang={lang} />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <Breadcrumb
          className="text-[var(--a-muted)] [&_a]:text-[var(--a-muted)] [&_a:hover]:text-[var(--a-primary)] [&_[aria-current]]:text-[var(--a-fg)]"
          items={[
            { label: t("breadcrumb.home"), href: "/" },
            { label: page.breadcrumb },
          ]}
        />
        <p className="mt-8 font-mono text-xs font-medium uppercase tracking-[0.14em] text-[var(--a-primary)]">
          {page.price}
        </p>
        <h1 className="font-display mt-3 text-3xl font-semibold tracking-tight text-[var(--a-fg)] sm:text-4xl">
          {page.h1}
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-[var(--a-muted)]">{page.lead}</p>

        <h2 className="mt-12 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--a-muted)]">
          {site.ui.forWhom}
        </h2>
        <ul className="mt-4 space-y-3 text-[var(--a-fg)]/90">
          {page.forWhom.map((x) => (
            <li key={x} className="flex gap-2.5 text-[15px] leading-relaxed">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--a-primary)]" aria-hidden />
              {x}
            </li>
          ))}
        </ul>

        <h2 className="mt-12 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--a-muted)]">
          {site.ui.includes}
        </h2>
        <ul className="mt-4 space-y-3 text-[var(--a-fg)]/90">
          {page.includes.map((x) => (
            <li key={x} className="flex gap-2.5 text-[15px] leading-relaxed">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--a-primary)]" aria-hidden />
              {x}
            </li>
          ))}
        </ul>

        <a
          href={SITE_TELEGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackContactClick("telegram")}
          className="mt-12 inline-flex min-h-11 items-center gap-2 rounded-xl bg-[var(--a-primary)] px-6 py-3 text-sm font-semibold text-[var(--a-on-primary)] transition duration-200 hover:bg-[var(--a-primary-hover)]"
        >
          {page.cta}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </a>
        <p className="mt-4 text-sm text-[var(--a-muted)]">
          USDT / UAH / PL invoice —{" "}
          <LocalizedLink to="/contacts" className="font-medium text-[var(--a-primary)] hover:text-[var(--a-primary-hover)]">
            {site.nav.contacts}
          </LocalizedLink>
        </p>
      </div>
    </SiteShell>
  );
}
